import { useQuery } from '@apollo/client';
import { Card, CardTitle } from 'components/Card/Card';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { REVENUE_REPORT } from 'services/reports/query';

export type RevenueTypeAmount = {
    readonly key: string;
    readonly label: string;
    readonly parent: string | null;
    readonly amount: string;
};

export type RevenueMonth = {
    readonly year: number;
    readonly month: number;
    readonly invoicedMembership: string;
    readonly invoicedOther: string;
    readonly paidMembership: string;
    readonly paidOther: string;
    readonly invoicedByType: readonly RevenueTypeAmount[];
    readonly paidByType: readonly RevenueTypeAmount[];
};

export type RevenueConsumption = {
    readonly period: 'TODAY' | 'MONTH' | 'YEAR';
    readonly total: string;
    readonly byType: readonly RevenueTypeAmount[];
};

type RevenueReportData = {
    readonly revenueReport: {
        readonly months: readonly RevenueMonth[];
        readonly consumption: readonly RevenueConsumption[];
    };
};

export const useRevenueReport = () => useQuery<RevenueReportData>(REVENUE_REPORT);

export const formatChf = (amount: string | number) =>
    new Intl.NumberFormat('de-CH', {
        style: 'currency',
        currency: 'CHF',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(amount));

// German uses the backend label; English maps the known keys.
export const useTypeLabel = () => {
    const { t, i18n } = useTranslation('dashboard');
    return (row: RevenueTypeAmount) => {
        if (!i18n.language?.startsWith('en')) return row.label;
        return t(`revenueType.${row.key}`, { defaultValue: row.label });
    };
};

type ConsumptionWidgetProps = {
    readonly period: RevenueConsumption['period'];
    readonly titleKey: string;
};

export const ConsumptionWidget = ({ period, titleKey }: ConsumptionWidgetProps) => {
    const { t } = useTranslation('dashboard');
    const typeLabel = useTypeLabel();
    const { data, loading, error } = useRevenueReport();
    const entry = data?.revenueReport.consumption.find((c) => c.period === period);
    return (
        <Card className="border flex-1">
            <CardTitle className="text-base">{t(titleKey)}</CardTitle>
            {loading && <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />}
            {!loading && (error || !entry) && <div className="text-sm text-red-600">{t('revenueError')}</div>}
            {!loading && !error && entry && (
                <>
                    <div className="text-2xl font-semibold text-zinc-800 mb-3">{formatChf(entry.total)}</div>
                    <ul className="text-sm text-zinc-600 flex flex-col gap-1">
                        {entry.byType.map((row) => (
                            <li
                                key={`${row.parent ?? ''}-${row.key}`}
                                className={`flex justify-between ${row.parent ? 'pl-4 text-zinc-500' : ''}`}
                            >
                                <span>{typeLabel(row)}</span>
                                <span>{formatChf(row.amount)}</span>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </Card>
    );
};
